import React from 'react';
import { Container } from 'react-bootstrap';

const PrivacyPolicyPage = () => {
  return (
    <Container className="py-5" style={{ maxWidth: '900px' }}>
      <div className="legal-document">
        <h1 className="mb-4">Privacy Policy</h1>
        
        <p className="text-muted mb-4">
          <strong>Last Updated: January 01, 2026</strong>
        </p>

        <section className="mb-5">
          <h2>1. Introduction</h2>
          <p>
            Better Business Booking ("Triple B", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, mobile applications, and related services (collectively, the "Service").
          </p>
          <p>
            Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, you should not use the Service.
          </p>
        </section>

        <section className="mb-5">
          <h2>2. Information We Collect</h2>
          <h3>2.1 Information You Provide to Us</h3>
          <p><strong>Account Information:</strong></p>
          <ul>
            <li>Email address</li>
            <li>Password (stored in encrypted form)</li>
            <li>Business name and description</li>
            <li>Contact information (phone, address)</li>
            <li>Business hours and timezone preferences</li>
            <li>Profile information and settings</li>
          </ul>
          <p><strong>Appointment and Booking Information:</strong></p>
          <ul>
            <li>Customer names</li>
            <li>Customer email addresses</li>
            <li>Customer phone numbers</li>
            <li>Appointment dates and times</li>
            <li>Service details</li>
            <li>Notes and special requests</li>
            <li>Payment information (processed through third-party payment processors)</li>
          </ul>
          <h3>2.2 Information Automatically Collected</h3>
          <p>We automatically collect usage data including IP address, browser type, device information, pages visited, and technical data such as cookies and log files.</p>
          <h3>2.3 Information from Third-Party Services</h3>
          <p>We may receive information from third-party services you connect, including:</p>
          <ul>
            <li>Calendar integrations (Google Calendar, Microsoft Outlook)</li>
            <li>Payment processors (Stripe)</li>
            <li>SMS services (Twilio)</li>
            <li>Accounting software (QuickBooks)</li>
          </ul>
        </section>

        <section className="mb-5">
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide and maintain the Service</li>
            <li>Process appointments and bookings</li>
            <li>Send notifications and reminders</li>
            <li>Process payments and manage subscriptions</li>
            <li>Sync with calendar services</li>
            <li>Provide customer support</li>
            <li>Improve our Service and develop new features</li>
            <li>Comply with legal obligations</li>
            <li>Prevent fraud and abuse</li>
          </ul>
        </section>

        <section className="mb-5">
          <h2>4. How We Share Your Information</h2>
          <p>We do not sell your personal information. We may share your information with:</p>
          <ul>
            <li><strong>Service Providers</strong>: Third-party service providers who perform services on our behalf (payment processors, email services, SMS services, calendar services, hosting providers)</li>
            <li><strong>Legal Requirements</strong>: When required by law or to protect our rights</li>
            <li><strong>Business Transfers</strong>: In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>With Your Consent</strong>: When you explicitly consent to sharing</li>
          </ul>
        </section>

        <section className="mb-5">
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your information, including encryption of data in transit and at rest, access controls, strong authentication mechanisms, regular security updates, and continuous monitoring. However, no method of transmission over the Internet or electronic storage is 100% secure.
          </p>
        </section>

        <section className="mb-5">
          <h2>6. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to provide the Service, comply with legal obligations, resolve disputes, and enforce our agreements. Account information is retained while your account is active and for a reasonable period after account closure. Payment records are retained as required by law (typically 7 years).
          </p>
        </section>

        <section className="mb-5">
          <h2>7. Your Privacy Rights</h2>
          <p>Depending on your location, you may have certain rights regarding your personal information:</p>
          <ul>
            <li><strong>Access</strong>: Right to access your personal information</li>
            <li><strong>Correction</strong>: Right to correct inaccurate information</li>
            <li><strong>Deletion</strong>: Right to request deletion of your information</li>
            <li><strong>Portability</strong>: Right to receive your data in a portable format</li>
            <li><strong>Objection</strong>: Right to object to certain processing</li>
            <li><strong>Restriction</strong>: Right to request restriction of processing</li>
            <li><strong>Withdrawal of Consent</strong>: Right to withdraw consent where processing is based on consent</li>
            <li><strong>Opt-Out</strong>: Right to opt-out of marketing communications</li>
          </ul>
          <p>To exercise your rights, please contact us at <a href="mailto:bbb@fringe.ltd">bbb@fringe.ltd</a>. We will respond within 30 days (or as required by applicable law).</p>
        </section>

        <section className="mb-5">
          <h2>8. Children's Privacy</h2>
          <p>
            Our Service is not intended for children under the age of 18. We do not knowingly collect personal information from children under 18. If you believe we have collected information from a child under 18, please contact us immediately.
          </p>
        </section>

        <section className="mb-5">
          <h2>9. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country of residence. When we transfer information internationally, we ensure appropriate safeguards are in place, such as standard contractual clauses or adequacy decisions.
          </p>
        </section>

        <section className="mb-5">
          <h2>10. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to remember your preferences, authenticate your identity, analyze usage, and provide personalized content. You can control cookies through your browser settings or our cookie preference center. Disabling certain cookies may affect the functionality of the Service.
          </p>
        </section>

        <section className="mb-5">
          <h2>11. Third-Party Services</h2>
          <p>
            Our Service contains links to and integrates with third-party services. This Privacy Policy does not apply to third-party services. We encourage you to review the privacy policies of third-party services you use, including:
          </p>
          <ul>
            <li><strong>Stripe</strong>: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a></li>
            <li><strong>Google</strong>: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
            <li><strong>Microsoft</strong>: <a href="https://privacy.microsoft.com/" target="_blank" rel="noopener noreferrer">https://privacy.microsoft.com/</a></li>
            <li><strong>Twilio</strong>: <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer">https://www.twilio.com/legal/privacy</a></li>
            <li><strong>QuickBooks</strong>: <a href="https://www.intuit.com/privacy/" target="_blank" rel="noopener noreferrer">https://www.intuit.com/privacy/</a></li>
          </ul>
        </section>

        <section className="mb-5">
          <h2>12. California Privacy Rights (CCPA)</h2>
          <p>
            If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, the right to delete your personal information, and the right to opt-out of the sale of personal information (we do not sell personal information). To exercise your CCPA rights, contact us at <a href="mailto:bbb@fringe.ltd">bbb@fringe.ltd</a>.
          </p>
        </section>

        <section className="mb-5">
          <h2>13. European Privacy Rights (GDPR)</h2>
          <p>
            If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR). We process your personal information based on consent, contract, legal obligation, or legitimate interests. If you have questions about our data processing activities, you may contact our Data Protection Officer at <a href="mailto:bbb@fringe.ltd">bbb@fringe.ltd</a>. You also have the right to lodge a complaint with your local data protection authority.
          </p>
        </section>

        <section className="mb-5">
          <h2>14. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated Privacy Policy on our website, sending an email to your registered email address, or displaying a notice within the Service. Your continued use of the Service after changes become effective constitutes acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section className="mb-5">
          <h2>15. Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
          </p>
          <p>
            <strong>Better Business Booking</strong><br />
            Email: <a href="mailto:bbb@fringe.ltd">bbb@fringe.ltd</a><br />
            Website: <a href="https://bbb.fringe.ltd" target="_blank" rel="noopener noreferrer">https://bbb.fringe.ltd</a>
          </p>
        </section>
      </div>

      <style>{`
        .legal-document {
          line-height: 1.8;
        }
        .legal-document h1 {
          color: #333;
          font-size: 2.5rem;
          font-weight: bold;
        }
        .legal-document h2 {
          color: #444;
          font-size: 1.5rem;
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        .legal-document h3 {
          color: #555;
          font-size: 1.2rem;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }
        .legal-document ul {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .legal-document li {
          margin-bottom: 0.5rem;
        }
        .legal-document p {
          margin-bottom: 1rem;
        }
        .legal-document a {
          color: #6366f1;
          text-decoration: none;
        }
        .legal-document a:hover {
          text-decoration: underline;
        }
      `}</style>
    </Container>
  );
};

export default PrivacyPolicyPage;
